import { Check, CheckCircle2, Copy, XCircle } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface MetaTagAnalysisProps {
  tags: {
    name: string;
    value?: string | null;
    required?: boolean;
  }[];
  title: string;
}

function MetaTag({ name, value, required }: MetaTagAnalysisProps["tags"][0]) {
  const [copied, setCopied] = useState(false);
  const trimmedValue = value?.trim() ?? "";
  const hasValue = trimmedValue !== "";
  const needsUrl = name === "og:image" || name === "twitter:image";
  const hasValidValue = hasValue && (!needsUrl || /^https?:\/\//i.test(trimmedValue));
  const showMissingBadge = required && !hasValue;
  const showCount = hasValue && (name.includes("title") || name.includes("description"));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trimmedValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable, ignore
    }
  };

  return (
    <div className="group flex items-center justify-between gap-3 border-b p-3">
      <div className="flex min-w-0 flex-shrink-0 items-center">
        {hasValidValue ? (
          <CheckCircle2 className="mr-2 h-5 w-5 flex-shrink-0 text-green-500" />
        ) : (
          <XCircle className="mr-2 h-5 w-5 flex-shrink-0 text-red-500" />
        )}
        <span className="font-mono text-sm">{name}</span>
        {showMissingBadge && (
          <Badge variant="destructive" className="ml-2">
            Missing
          </Badge>
        )}
        {showCount && (
          <span className="text-muted-foreground ml-2 text-xs whitespace-nowrap">{trimmedValue.length} chars</span>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-1">
        <span className="truncate text-sm text-gray-500" title={hasValue ? trimmedValue : undefined}>
          {hasValue ? trimmedValue : value}
        </span>
        {hasValue && (
          <button
            type="button"
            onClick={() => void handleCopy()}
            aria-label={`Copy ${name} value`}
            className="text-muted-foreground hover:text-foreground flex-shrink-0 rounded p-1 opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MetaTagAnalysis({ tags, title }: MetaTagAnalysisProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {tags.map((tag) => (
            <MetaTag key={tag.name} {...tag} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
